import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase, getEventForLive, getTracksForLive, getMusicians, getAppSetting, markTrackPlayed, resetEventLikes, updateEventProposalsOpen, updateEventNextTrack, updateTrackStatus, addTrack, updateTrack, joinTrack, leaveTrack, removeTrack, likeTrack, unlikeTrack, addApplause, resetApplause, getEventOrganizers } from '../lib/supabase';
import {
  computePriorityScore, sortByPriority, sortByScaletta, sortByRotazione,
  countPlayedPerMusicianInstrument, getMusiciansPerInstrument,
  computeCurrentFrequencyDistance, computeTrackFrequencyDelta,
} from '../scoring';
import { firstName, isTrackReady, normalizeInstrument, splitRoles, INSTRUMENT_ORDER } from '../helpers';
import TrackModal from '../modals/TrackModal';
import JammoMark from '../ui/JammoMark';
import Modal from '../ui/Modal';
import MusicianPopup from '../ui/MusicianPopup';

const DRAFT_SLOTS = [
  { icon:'/voice.png',  label:'Voce',     match: r => r.toLowerCase().includes('voce') },
  { icon:'/keys.png',   label:'Piano/Keys', match: r => r.toLowerCase().includes('tast') || r.toLowerCase().includes('piano') || r.toLowerCase().includes('keys') },
  { icon:'/guitar.png', label:'Chitarra', match: r => r.toLowerCase().includes('chitar') },
];

function LiveView({ eventId, currentUser, onExitLive, defaultVenueBg = null }) {
  const [event, setEvent]   = useState(null);
  const [tracks, setTracks] = useState([]);
  const [heroBg, setHeroBg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playedCollapsed, setPlayedCollapsed] = useState(false);
  const [countersCollapsed, setCountersCollapsed] = useState(true);
  const [legendaAperta, setLegendaAperta] = useState(false);
  const [nonProntiCollapsed, setNonProntiCollapsed] = useState(false);
  const [nextTrackId, setNextTrackId] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [proposalsOpen, setProposalsOpen] = useState(true);
  const [editTrack, setEditTrack] = useState(null);
  const [addTrackOpen, setAddTrackOpen] = useState(false);
  const [trackDetail, setTrackDetail] = useState(null);
  const [musicians, setMusicians] = useState([]);
  const [profileModal, setProfileModal] = useState(null);
  const [musicianModal, setMusicianModal] = useState(null);
  const [filterMusicianIds, setFilterMusicianIds] = useState(new Set());
  const [sortMode, setSortMode] = useState('scaletta');
  const [sortDir, setSortDir] = useState('asc');


  const likeKey = currentUser
    ? `jamup_likes_${eventId}_u${currentUser.id}`
    : `jamup_likes_${eventId}_guest`;
  const [likedIds, setLikedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(likeKey) || '[]')); }
    catch { return new Set(); }
  });

  // Anche i co-organizzatori vedono la vista di regia. L'elenco si legge solo
  // da loggati: il pubblico col QR non ne ha bisogno e non puo' leggerlo.
  const [coOrganizzatori, setCoOrganizzatori] = useState([]);
  useEffect(() => {
    if (!currentUser) return;
    getEventOrganizers(eventId).then(setCoOrganizzatori).catch(() => {});
  }, [currentUser, eventId]);
  const isOrganizer = !!(currentUser && event && (
    String(currentUser.id) === String(event.manager_id)
    || coOrganizzatori.some(id => String(id) === String(currentUser.id))));

  // ── APPLAUSI ────────────────────────────────────────────────────────────────
  // Si applaude solo il brano che sta suonando, e solo mentre suona. Lo puo'
  // fare chiunque: pubblico col QR, musicisti, organizzatore.
  //
  // I tocchi non partono uno per uno. A fine pezzo cinquanta persone che
  // tempestano il pulsante farebbero centinaia di scritture al secondo: qui si
  // accumulano e partono insieme dopo mezzo secondo di pausa.
  //
  // Ognuno ne ha 20 per brano. Il conto resta sul telefono (localStorage),
  // cosi' ricaricare la pagina non li rimette a disposizione.
  const APPLAUSI_MAX = 20;                          // per persona, per brano
  const applausiKey = likeKey.replace('jamup_likes_', 'jamup_applausi_');
  const [ordineSuonati, setOrdineSuonati] = useState('serata'); // serata | applausi
  const [mieiApplausi, setMieiApplausi] = useState(() => {      // trackId -> quanti ne ho dati
    try { return JSON.parse(localStorage.getItem(applausiKey) || '{}') || {}; }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(applausiKey, JSON.stringify(mieiApplausi)); } catch {}
  }, [applausiKey, mieiApplausi]);
  const [applausiInVolo, setApplausiInVolo] = useState(0); // toccati ma non confermati
  const [totaliNoti, setTotaliNoti] = useState({});        // trackId -> totale dal server
  const daInviare = useRef(0);
  const flushTimer = useRef(null);

  const inviaApplausi = useCallback(async (trackId) => {
    const n = daInviare.current;
    daInviare.current = 0;
    if (!n) { setApplausiInVolo(0); return; }
    try {
      const totale = await addApplause(trackId, n);
      // La funzione restituisce il totale aggiornato, oppure null se nel
      // frattempo il brano ha smesso di suonare.
      if (totale != null) setTotaliNoti(t => ({ ...t, [trackId]: totale }));
    } catch (e) { console.error(e); }
    finally { setApplausiInVolo(0); }
  }, []);

  const handleApplause = (track) => {
    const dati = mieiApplausi[track.id] || 0;
    if (dati >= APPLAUSI_MAX) return;
    setMieiApplausi(m => ({ ...m, [track.id]: dati + 1 }));
    setApplausiInVolo(v => v + 1);
    daInviare.current += 1;
    clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => inviaApplausi(track.id), 500);
  };

  // Quanti applausi mostrare: il piu' alto fra quello che sa il server e
  // quello che sappiamo noi, piu' i tocchi appena fatti.
  const applausiDi = (track, conInVolo = false) =>
    Math.max(track?.applause || 0, totaliNoti[track?.id] || 0) + (conInVolo ? applausiInVolo : 0);

  // I suonati si guardano in due modi: nell'ordine in cui sono andati in scena,
  // o dal piu' applaudito. Il secondo e' il resoconto della serata.
  const ordinaSuonati = (lista) =>
    ordineSuonati === 'applausi'
      ? [...lista].sort((a, b) => applausiDi(b) - applausiDi(a))
      : lista;

  const renderSceltaOrdine = () => (
    <div style={{ display:'flex', gap:6, marginBottom:8 }}>
      {[['serata','Ordine di serata'], ['applausi','Piu applauditi']].map(([val, label]) => (
        <button key={val} onClick={() => setOrdineSuonati(val)}
          style={{ padding:'8px 14px', minHeight:44, borderRadius:999, border:'none', cursor:'pointer',
            fontSize:16, fontWeight:700, fontFamily:'inherit',
            background: ordineSuonati === val ? '#ED4D1D' : 'rgba(237,77,29,0.08)',
            color: ordineSuonati === val ? '#fff' : '#B5AA9A' }}>
          {label}
        </button>
      ))}
    </div>
  );

  const renderContoApplausi = (track) => {
    const n = applausiDi(track);
    if (!n) return null;
    return (
      <span style={{ fontSize:17, fontWeight:800, color:'#ED4D1D', flexShrink:0, paddingLeft:8 }}>
        {n}
      </span>
    );
  };

  // Funzione e non componente: un componente ridefinito a ogni render verrebbe
  // rimontato da React a ogni applauso, e l'animazione della pressione
  // sparirebbe a meta'.
  const renderApplausi = (track, grande) => {
    const dati = mieiApplausi[track.id] || 0;
    const esaurito = dati >= APPLAUSI_MAX;
    const totale = applausiDi(track, true);
    return (
      <button
        onClick={e => { e.stopPropagation(); handleApplause(track); }}
        disabled={esaurito}
        style={{
          display:'flex', alignItems:'center', justifyContent:'center', gap:12,
          margin:'16px auto 0', minHeight: grande ? 56 : 48,
          padding: grande ? '12px 34px' : '10px 26px',
          borderRadius:999, border:'none',
          cursor: esaurito ? 'default' : 'pointer',
          background: esaurito ? 'rgba(237,77,29,0.18)' : '#ED4D1D',
          color:'#fff', fontFamily:'inherit', transition:'transform .06s',
        }}
        onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.94)'; }}
        onPointerUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
        <span style={{ fontSize: grande ? 20 : 18, fontWeight:800 }}>
          {esaurito ? 'Grazie!' : 'Applaudi'}
        </span>
        {totale > 0 && (
          <span style={{ fontSize: grande ? 22 : 20, fontWeight:900,
            paddingLeft:12, borderLeft:'1px solid rgba(255,255,255,0.35)' }}>
            {totale}
          </span>
        )}
      </button>
    );
  };

  // Se il brano smette di suonare mentre si sta applaudendo, quello che resta
  // in coda parte subito invece di andare perso.
  useEffect(() => () => clearTimeout(flushTimer.current), []);


  const load = useCallback(async () => {
    try {
      const [ev, tr] = await Promise.all([getEventForLive(eventId), getTracksForLive(eventId)]);
      setEvent(ev);
      setTracks(tr.sort((a, b) => (a.position ?? 999) - (b.position ?? 999)));
      setNextTrackId(ev?.next_track_id || null);
      // Solo per chi ha un account: serve all'organizzatore per gestire i
      // brani. Chi guarda dal QR i nomi li riceve gia' insieme alla scaletta,
      // e non deve poter arrivare ai contatti di nessuno.
      if (currentUser) getMusicians().then(mus => setMusicians(mus || [])).catch(() => {});
      // Niente locandina qui: il pubblico e' gia' nel locale, e una locandina
      // piena di scritte sotto il titolo e' solo rumore.
      const bg = ev?.venueBg || defaultVenueBg
        || await getAppSetting('default_venue_bg').catch(() => null);
      setHeroBg(bg || null);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [eventId, defaultVenueBg, currentUser]);

  useEffect(() => { load(); }, [load]);

  // Durante la serata arrivano molti cambiamenti ravvicinati (ogni like del
  // pubblico e' un evento). Senza freno ognuno farebbe ricaricare l'intera
  // scaletta su tutti i telefoni collegati: qui raggruppiamo le notifiche
  // arrivate nello stesso mezzo secondo in un unico ricaricamento.
  const reloadTimer = useRef(null);
  const scheduleReload = useCallback(() => {
    if (reloadTimer.current) return;
    reloadTimer.current = setTimeout(() => { reloadTimer.current = null; load(); }, 500);
  }, [load]);

  useEffect(() => {
    const chTracks = supabase.channel(`live-tracks-${eventId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tracks', filter: `event_id=eq.${eventId}` }, scheduleReload)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tracks', filter: `event_id=eq.${eventId}` }, scheduleReload)
      .subscribe();
    const chEvent = supabase.channel(`live-event-${eventId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'events', filter: `id=eq.${eventId}` }, scheduleReload)
      .subscribe();
    return () => {
      if (reloadTimer.current) { clearTimeout(reloadTimer.current); reloadTimer.current = null; }
      supabase.removeChannel(chTracks);
      supabase.removeChannel(chEvent);
    };
  }, [eventId, scheduleReload]);

  const saveLiked = (ids) => localStorage.setItem(likeKey, JSON.stringify([...ids]));

  const handleLike = async (track) => {
    if (track.played) return;
    const already = likedIds.has(track.id);
    const newIds = new Set(likedIds);
    const delta = already ? -1 : 1;
    if (already) newIds.delete(track.id); else newIds.add(track.id);
    setLikedIds(newIds); saveLiked(newIds);
    setTracks(prev => prev.map(t => t.id === track.id ? { ...t, likes: Math.max(0, (t.likes || 0) + delta) } : t));
    // Il conteggio vero lo fa il database: cosi due persone che votano nello
    // stesso istante non si cancellano il voto a vicenda.
    if (already) await unlikeTrack(track.id);
    else await likeTrack(track.id);
  };

  const handlePlayed = async (track) => {
    const newVal = !track.played;
    await markTrackPlayed(track.id, newVal);
    if (!newVal && track.status === 'playing') {
      await updateTrackStatus(track.id, 'auto');
    }
    if (newVal && String(nextTrackId) === String(track.id)) {
      setNextTrackId(null);
      updateEventNextTrack(eventId, null).catch(console.error);
    }
    await load();
  };

  const handleSetPlaying = async (track) => {
    // Se c'era già un brano in esecuzione, riportalo ad 'auto'
    const prev = tracks.find(t => t.status === 'playing');
    if (prev && prev.id !== track.id) await updateTrackStatus(prev.id, 'auto');
    await updateTrackStatus(track.id, 'playing');
    // Rimuovi il pin "prossimo" se coincide
    if (String(nextTrackId) === String(track.id)) {
      setNextTrackId(null);
      updateEventNextTrack(eventId, null).catch(console.error);
    }
    await load();
  };

  const handleStopPlaying = async (track) => {
    await markTrackPlayed(track.id, true);
    await load();
  };

  // Porta alla registrazione uscendo dalla pagina live: l'indirizzo va
  // riscritto e la pagina ricaricata, perche' l'app decide cosa mostrare
  // leggendo l'indirizzo una volta sola all'avvio.
  const vaiAllaRegistrazione = () => {
    window.location.href = `${window.location.origin}${window.location.pathname}#/registrati`;
    window.location.reload();
  };

  const handleResetLikes = async () => {
    await resetEventLikes(eventId);
    setTracks(prev => prev.map(t => ({ ...t, likes: 0 })));
    setLikedIds(new Set()); saveLiked(new Set());
    setConfirmReset(false);
  };

  // Cosa manca perche' un brano sia "pronto" (una voce e uno strumento
  // armonico). Non blocca niente: dal vivo il cantante si aggiunge sul palco.
  const mancaA = (track) => {
    const ruoli = (track?.track_players || []).flatMap(p => splitRoles(p.role).map(normalizeInstrument));
    const manca = [];
    if (!ruoli.includes('Voce')) manca.push('Voce');
    if (!ruoli.includes('Chitarra') && !ruoli.includes('Piano/Keys')) manca.push('Chitarra o Piano/Keys');
    return manca;
  };

  // ── DATI DERIVATI ─────────────────────────────────────────────────────────
  const playingTrack = tracks.find(t => t.status === 'playing' && !t.played) || null;
  const played   = tracks.filter(t => t.played);

  const playsMap        = useMemo(() => countPlayedPerMusicianInstrument(tracks), [tracks]);
  const musiciansMap    = useMemo(() => getMusiciansPerInstrument(tracks), [tracks]);
  const currentDistance = useMemo(() => computeCurrentFrequencyDistance(playsMap, musiciansMap), [playsMap, musiciansMap]);
  const hasScaletta     = useMemo(() => tracks.some(t => t.in_scaletta && !t.played), [tracks]);

  useEffect(() => {
    if (!hasScaletta && sortMode === 'scaletta') setSortMode('priority');
  }, [hasScaletta]); // eslint-disable-line

  // Nomi musicisti da tutti i track_players
  const nameMap = useMemo(() => {
    const map = {};
    for (const track of tracks) {
      for (const player of (track.track_players || [])) {
        const id = String(player.musician_id);
        if (!map[id]) map[id] = player.musicians?.name || 'Musicista';
      }
    }
    return map;
  }, [tracks]);

  // Musicisti raggruppati per strumento, ordinati per nome, con contatore per-strumento
  // Nome e ordine vengono da helpers.js: qui non si ridichiarano.
  const PLAYER_ORDER = INSTRUMENT_ORDER;
  const musiciansByInstrument = useMemo(() => {
    const result = {};
    for (const instr of INSTRUMENT_ORDER) {
      const set = musiciansMap[instr];
      if (!set || set.size === 0) continue;
      result[instr] = [...set]
        .map(id => ({ id, name: nameMap[id] || 'Musicista', count: playsMap[`${id}__${instr}`] || 0 }))
        .sort((a, b) => b.count - a.count || (a.name || '').localeCompare(b.name || ''));
    }
    return result;
  }, [musiciansMap, nameMap, playsMap]);

  const openMusicianModal = (musicianId) => {
    const id = String(musicianId);
    const fullName = nameMap[id] || 'Musicista';
    const instruments = INSTRUMENT_ORDER.filter(instr => musiciansMap[instr]?.has(id));
    const mTracks = tracks.filter(t => (t.track_players || []).some(p => String(p.musician_id) === id));
    const playedCount  = mTracks.filter(t => t.played).length;
    const bookedCount  = mTracks.filter(t => !t.played).length;
    setMusicianModal({ id, fullName, instruments, playedCount, bookedCount });
  };


  const handleSortModeClick = (mode) => {
    if (sortMode === mode) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortMode(mode);
      setSortDir('asc');
    }
  };

  const toggleMusicianFilter = (id) => {
    setFilterMusicianIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Brani ordinati in base alla modalita scelta, con override "prossimo"
  const rankedUnplayed = useMemo(() => {
    if (!isOrganizer) return [];
    let sorted;
    if (sortMode === 'scaletta') {
      sorted = sortByScaletta(tracks);
    } else if (sortMode === 'rotazione') {
      sorted = sortByRotazione(tracks, playsMap, musiciansMap, currentDistance);
    } else {
      sorted = sortByPriority(tracks);
    }
    let unplayedSorted = sorted.filter(t => !t.played && t.status !== 'playing');
    if (sortDir === 'desc') unplayedSorted = [...unplayedSorted].reverse();
    if (!nextTrackId) return unplayedSorted;
    const idx = unplayedSorted.findIndex(t => String(t.id) === String(nextTrackId));
    if (idx <= 0) return unplayedSorted;
    const result = [...unplayedSorted];
    const [next] = result.splice(idx, 1);
    result.unshift(next);
    return result;
  }, [isOrganizer, tracks, nextTrackId, sortMode, sortDir, playsMap, musiciansMap, currentDistance]);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#181614', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div className="spinner"/>
    </div>
  );
  if (!event) return (
    <div style={{ minHeight:'100vh', background:'#181614', display:'flex', alignItems:'center', justifyContent:'center', color:'#B5AA9A', fontSize:17 }}>
      Evento non trovato
    </div>
  );

  // ── RENDER ORGANIZZATORE ──────────────────────────────────────────────────
  if (isOrganizer) {
    const pinnedNextTrack = nextTrackId
      ? tracks.find(t => String(t.id) === String(nextTrackId) && !t.played && t.status !== 'playing')
      : null;

    return (
      <>
      <div style={{ minHeight:'100vh', background:'#000000', fontFamily:"'Exo 2',sans-serif", color:'#E8DDD0' }}>

        {/* Header — uguale alla vista ospite */}
        <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:10, background:'#000000', borderBottom:'1px solid rgba(237,77,29,0.06)', height:'calc(80px + max(env(safe-area-inset-top),16px))', paddingTop:'max(env(safe-area-inset-top),16px)', paddingLeft:12, paddingRight:12, display:'flex', alignItems:'center', justifyContent:'space-between', boxSizing:'border-box' }}>
          <JammoMark height={58} style={{ flexShrink:0, cursor: onExitLive ? 'pointer' : 'default' }} onClick={() => onExitLive && onExitLive()}/>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0, background:'var(--surface2)', border:'1px solid var(--card-border)', borderRadius:999, padding:'6px 16px 6px 6px' }}>
            <div style={{ width:30, height:30, borderRadius:'50%', background:'var(--grad)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#fff', flexShrink:0 }}>
              {(currentUser?.name || 'O').charAt(0).toUpperCase()}
            </div>
            <span style={{ fontSize:16, fontWeight:600, color:'var(--text)', maxWidth:100, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
              {firstName(currentUser?.name || '')}
            </span>
          </div>
        </div>

        {/* Striscia LIVE */}
        <div style={{ position:'fixed', top:'calc(80px + max(env(safe-area-inset-top),16px))', left:0, right:0, zIndex:9, background:'#ED4D1D', height:26, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
          <span className="live-dot" style={{ width:7, height:7, borderRadius:'50%', background:'#fff', display:'inline-block', flexShrink:0 }}/>
          <span style={{ fontSize:15, fontWeight:900, color:'#fff', letterSpacing:'4px' }}>LIVE</span>
        </div>

        <div style={{ padding:'calc(114px + max(env(safe-area-inset-top),16px)) 16px calc(80px + env(safe-area-inset-bottom, 0px))' }}>

          {/* 1. ON STAGE */}
          {playingTrack && (
            <div onClick={() => setEditTrack(playingTrack)} style={{ marginBottom:24, padding:'24px 16px 20px', borderRadius:14, background:'#1F1C18', border:'5px solid #ED4D1D', cursor:'pointer' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:7, marginBottom:16 }}>
                <span className="live-dot" style={{ width:8, height:8, borderRadius:'50%', background:'#ED4D1D', display:'inline-block', flexShrink:0 }}/>
                <span style={{ fontSize:17, fontWeight:800, color:'#ED4D1D', letterSpacing:'3px' }}>ON STAGE</span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ fontSize:19, fontWeight:500, color:'#706A60', minWidth:18, flexShrink:0 }}>{played.length + 1}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:20, fontWeight:700, color:'#E8DDD0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {playingTrack.title}
                    {playingTrack.artist && <span style={{ fontWeight:400, color:'#706A60' }}> — {playingTrack.artist}</span>}
                  </div>
                </div>
              </div>
              {renderApplausi(playingTrack, true)}
              <button onClick={e => { e.stopPropagation(); handleStopPlaying(playingTrack); }} style={{ display:'block', margin:'14px auto 0', padding:'10px 28px', borderRadius:8, background:'none', color:'#B5AA9A', border:'1px solid rgba(255,255,255,0.2)', fontSize:17, fontWeight:700, cursor:'pointer', minHeight:44, fontFamily:'inherit' }}>
                Suonato
              </button>
            </div>
          )}

          {/* 2. IN PREPARAZIONE */}
          {pinnedNextTrack && (
            <div style={{ marginBottom:24, padding:'20px 16px 16px', borderRadius:14, background:'#1F1C18', border:'2px solid #ED4D1D' }}>
              <div style={{ fontSize:17, fontWeight:800, color:'#ED4D1D', letterSpacing:'3px', textAlign:'center', marginBottom:14 }}>
                IN PREPARAZIONE
              </div>
              <div onClick={() => setEditTrack(pinnedNextTrack)} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom:10 }}>
                <div style={{ fontSize:19, fontWeight:500, color:'#706A60', minWidth:18, flexShrink:0 }}>{played.length + (playingTrack ? 1 : 0) + 1}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:20, fontWeight:700, color:'#E8DDD0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {pinnedNextTrack.title}
                    {pinnedNextTrack.artist && <span style={{ fontWeight:400, color:'#706A60' }}> — {pinnedNextTrack.artist}</span>}
                  </div>
                </div>
              </div>
              {(pinnedNextTrack.track_players || []).filter(p => p.role).length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:12, paddingLeft:26 }}>
                  {[...(pinnedNextTrack.track_players || []).filter(p => p.role)]
                    .sort((a, b) => {
                      const ai = PLAYER_ORDER.indexOf(normalizeInstrument(a.role));
                      const bi = PLAYER_ORDER.indexOf(normalizeInstrument(b.role));
                      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                    })
                    .map(p => {
                      const name = p.musicians?.name || nameMap[String(p.musician_id)] || 'Musicista';
                      const role = p.role.replace('(alt)', '').trim();
                      return (
                        <span key={p.musician_id} style={{ fontSize:15, padding:'3px 8px', borderRadius:6, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', color:'#E8DDD0' }}>
                          {role}{role && name ? ' · ' : ''}{firstName(name)}
                        </span>
                      );
                    })}
                </div>
              )}
              {mancaA(pinnedNextTrack).length > 0 && (
                <div style={{ fontSize:16, color:'#FFA726', marginBottom:12, paddingLeft:26 }}>
                  Manca: {mancaA(pinnedNextTrack).join(', ')}
                </div>
              )}
              <div style={{ display:'flex', gap:8 }}>
                <button
                  onClick={() => { setNextTrackId(null); updateEventNextTrack(eventId, null).catch(console.error); }}
                  style={{ flex:1, padding:'7px 4px', borderRadius:8, minHeight:44, cursor:'pointer', fontSize:16, fontWeight:700, fontFamily:'inherit', background:'rgba(237,77,29,0.06)', color:'#ED4D1D', border:'1px solid rgba(237,77,29,0.2)' }}>
                  Annulla
                </button>
                <button
                  onClick={() => handleSetPlaying(pinnedNextTrack)}
                  style={{ flex:1, padding:'7px 4px', borderRadius:8, minHeight:44, cursor:'pointer', fontSize:16, fontWeight:700, fontFamily:'inherit', background:'#ED4D1D', color:'#fff', border:'none' }}>
                  In esecuzione
                </button>
              </div>
            </div>
          )}

          {/* 3. Titolo sezione */}
          <div style={{ fontSize:22, fontWeight:800, color:'#E8DDD0', marginBottom:16, marginTop: (playingTrack || pinnedNextTrack) ? 0 : 8 }}>
            Scegli il prossimo brano
          </div>

          {/* 4. Musicisti */}
          {Object.keys(musiciansByInstrument).length > 0 && (() => {
            const INSTR_ICONS = {
              'Voce':'voice.png', 'Piano/Keys':'keys.png', 'Chitarra':'guitar.png',
              'Basso':'bass.png', 'Batteria':'drums.png', 'Altro':'other.png',
            };
            const hasPlays = new Set(Object.keys(playsMap).map(k => k.split('__')[0]));
            const allIds = new Set();
            for (const musicians of Object.values(musiciansByInstrument)) {
              for (const m of musicians) allIds.add(m.id);
            }
            const zeroCount = [...allIds].filter(id => !hasPlays.has(id)).length;
            return (
              <div style={{ marginBottom:16, background:'#1F1C18', borderRadius:14, border:'1px solid rgba(255,255,255,0.07)', overflow:'hidden' }}>
                <button onClick={() => setCountersCollapsed(o => !o)} style={{
                  width:'100%', background:'none', border:'none', cursor:'pointer',
                  padding:'14px 16px 12px', textAlign:'left',
                }}>
                  <div style={{ display:'flex', alignItems:'center', marginBottom:8 }}>
                    <span style={{ fontSize:17, fontWeight:800, color:'#E8DDD0', flex:1 }}>Musicisti</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#706A60" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, transition:'transform .2s', transform: countersCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}><polyline points="6 9 12 15 18 9"/></svg>
                  </div>
                  <div style={{ fontSize:15, color:'#B5AA9A', marginBottom:5 }}>
                    Brani suonati: <span style={{ fontWeight:700, color:'#E8DDD0' }}>{played.length}</span>
                  </div>
                  <div style={{ fontSize:15, color: zeroCount > 0 ? '#FFA726' : '#706A60' }}>
                    {zeroCount > 0
                      ? `${zeroCount} musicist${zeroCount === 1 ? 'a' : 'i'} senza brani suonati`
                      : 'Tutti hanno suonato almeno un brano'}
                  </div>
                </button>

                {!countersCollapsed && (
                  <div style={{ padding:'4px 0 0', display:'flex', flexDirection:'column' }}>
                    {INSTRUMENT_ORDER.filter(instr => musiciansByInstrument[instr]).map((instr, instrIdx, arr) => (
                      <div key={instr}>
                        {instrIdx > 0 && <div style={{ height:1, background:'rgba(255,255,255,0.07)', margin:'0 16px' }}/>}
                        <div style={{ display:'flex', gap:14, alignItems:'flex-start', padding:'12px 16px' }}>
                        <div style={{ width:40, height:40, borderRadius:10, background:'#0A0907', border:'1.5px solid #ED4D1D', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
                          <img src={`/${INSTR_ICONS[instr]}`} alt={instr} style={{ width:26, height:26, objectFit:'contain', filter:'invert(1)' }}/>
                        </div>
                        <div style={{ flex:1, display:'flex', flexDirection:'column', gap:0 }}>
                          {musiciansByInstrument[instr].map(m => {
                            const isFiltered = filterMusicianIds.has(m.id);
                            return (
                              <button key={m.id} onClick={() => openMusicianModal(m.id)} style={{
                                background: isFiltered ? 'rgba(237,77,29,0.1)' : 'none',
                                border:'none', borderRadius:7,
                                padding:'5px 8px',
                                display:'flex', alignItems:'center', justifyContent:'space-between',
                                cursor:'pointer', width:'100%', textAlign:'left', minHeight:34,
                              }}>
                                <span style={{ fontSize:17, fontWeight: isFiltered ? 700 : 400, color: isFiltered ? '#ED4D1D' : '#E8DDD0' }}>
                                  {firstName(m.name)}
                                </span>
                                <span style={{ fontSize:17, fontWeight:700, color: m.count > 0 ? '#E8DDD0' : '#4A4540', minWidth:20, textAlign:'right' }}>
                                  {m.count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Filtri musicista attivi */}
          {filterMusicianIds.size > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
              {[...filterMusicianIds].map(id => (
                <button key={id} onClick={() => toggleMusicianFilter(id)} style={{
                  display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
                  borderRadius:20, border:'1px solid rgba(237,77,29,0.4)',
                  background:'rgba(237,77,29,0.12)', color:'#ED4D1D',
                  fontSize:15, fontWeight:700, cursor:'pointer',
                }}>
                  {firstName(nameMap[id] || 'Musicista')}
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              ))}
              {filterMusicianIds.size > 1 && (
                <button onClick={() => setFilterMusicianIds(new Set())} style={{
                  display:'flex', alignItems:'center', gap:4, padding:'6px 10px',
                  borderRadius:20, border:'1px solid rgba(255,255,255,0.1)',
                  background:'rgba(255,255,255,0.04)', color:'#706A60',
                  fontSize:16, fontWeight:600, cursor:'pointer',
                }}>
                  Rimuovi tutti
                </button>
              )}
            </div>
          )}

          {/* 5. Bottoni azione */}
          <div style={{ display:'flex', gap:8, marginBottom:10 }}>
            <button
              onClick={() => setAddTrackOpen(true)}
              style={{
                flex:1, padding:'9px 12px', borderRadius:10, cursor:'pointer',
                fontSize:16, fontWeight:700, transition:'all .15s',
                background:'rgba(255,255,255,0.06)', color:'#B5AA9A',
                border:'1px solid rgba(255,255,255,0.1)',
              }}
            >
              + Brano
            </button>
            <button
              onClick={async () => {
                const next = !proposalsOpen;
                setProposalsOpen(next);
                await updateEventProposalsOpen(eventId, next);
              }}
              aria-pressed={proposalsOpen}
              style={{
                flex:1, padding:'9px 12px', borderRadius:10, cursor:'pointer', minHeight:44,
                display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                fontSize:16, fontWeight:700, transition:'all .15s', fontFamily:'inherit',
                background:'rgba(255,255,255,0.06)', color:'#B5AA9A',
                border:'1px solid rgba(255,255,255,0.1)',
              }}
            >
              Proposte
              <span style={{ padding:'2px 10px', borderRadius:999, fontSize:15, fontWeight:800,
                background: proposalsOpen ? '#ED4D1D' : 'rgba(255,255,255,0.08)',
                color: proposalsOpen ? '#fff' : '#8A8377' }}>
                {proposalsOpen ? 'Aperte' : 'Chiuse'}
              </span>
            </button>
          </div>

          {/* Cosa vogliono dire i tre numeri sulle schede dei brani. */}
          <div style={{ marginBottom:12 }}>
            <button onClick={() => setLegendaAperta(o => !o)} aria-expanded={legendaAperta}
              style={{ background:'none', border:'none', cursor:'pointer', padding:'6px 0', minHeight:44,
                fontSize:16, fontWeight:700, color:'#B5AA9A', fontFamily:'inherit', textDecoration:'underline',
                textDecorationColor:'rgba(181,170,154,0.4)' }}>
              {legendaAperta ? 'Chiudi la spiegazione dei numeri' : 'Cosa vogliono dire i numeri sui brani?'}
            </button>
            {legendaAperta && (
              <div style={{ display:'flex', flexDirection:'column', gap:10, padding:'12px 14px', borderRadius:10,
                background:'#1F1C18', border:'1px solid rgba(255,255,255,0.07)', fontSize:16, color:'#B5AA9A', lineHeight:1.45 }}>
                <div style={{ display:'flex', gap:10 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B5AA9A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flexShrink:0, marginTop:2}}><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                  <span><b style={{ color:'#E8DDD0' }}>Gradimento.</b> I like del pubblico, piu' un bonus per quanti musicisti si sono candidati.</span>
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B5AA9A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flexShrink:0, marginTop:2}}><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>
                  <span><b style={{ color:'#E8DDD0' }}>Musicisti.</b> Quanti suonano o cantano quel brano.</span>
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B5AA9A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flexShrink:0, marginTop:2}}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  <span><b style={{ color:'#E8DDD0' }}>Turni.</b> In verde col meno: fa suonare chi ha suonato poco. In arancione col piu': fa suonare di nuovo chi ha gia' suonato tanto. Zero: non cambia niente.</span>
                </div>
              </div>
            )}
          </div>

          {/* Ordinamento */}
          <div style={{ display:'flex', gap:6, marginBottom:14 }}>
            {[
              { mode:'scaletta',  label:'Scaletta'  },
              { mode:'priority',  label:'Like'      },
              { mode:'rotazione', label:'Rotazione' },
            ].map(({ mode, label }) => {
              const disabled = mode === 'scaletta' && !hasScaletta;
              const active = sortMode === mode && !disabled;
              return (
                <button
                  key={mode}
                  onClick={() => !disabled && handleSortModeClick(mode)}
                  style={{
                    flex:1, padding:'8px 4px', borderRadius:8,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center', gap:5,
                    transition:'all .15s',
                    border: active ? 'none' : '1px solid rgba(255,255,255,0.1)',
                    background: active ? '#ED4D1D' : 'rgba(255,255,255,0.04)',
                    color: active ? '#fff' : '#B5AA9A',
                    opacity: disabled ? 0.35 : 1,
                  }}
                >
                  <span style={{ fontSize:15, fontWeight:700 }}>{label}</span>
                  <svg width="14" height="14" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" style={{ opacity: active ? 1 : 0.4, flexShrink:0 }}>
                    <line x1="2" y1="3" x2="9" y2="3"/>
                    <line x1="2" y1="7" x2="7" y2="7"/>
                    <line x1="2" y1="11" x2="5" y2="11"/>
                    {active && sortDir === 'desc'
                      ? <polyline points="12,4 12,11 10,9" strokeLinejoin="round"/>
                      : <polyline points="12,11 12,4 10,6" strokeLinejoin="round"/>}
                  </svg>
                </button>
              );
            })}
          </div>

          {/* 6+. Lista brani */}
          {(() => {
            const filtered = rankedUnplayed.filter(t =>
              String(t.id) !== String(nextTrackId) &&
              (filterMusicianIds.size === 0 ||
              (t.track_players || []).some(p => filterMusicianIds.has(String(p.musician_id))))
            );
            if (filtered.length === 0 && !pinnedNextTrack) {
              const testo = filterMusicianIds.size > 0
                ? 'Nessun brano da suonare per chi hai selezionato'
                : 'Tutti i brani sono stati suonati';
              return <div style={{ textAlign:'center', padding:'32px 0', color:'#706A60', fontSize:17 }}>{testo}</div>;
            }
            const readyList = filtered.filter(t => isTrackReady(t));
            const draftList = filtered.filter(t => !isTrackReady(t));

            const renderTrackCard = (t, globalIdx, isReady) => {
              const rank           = played.length + (playingTrack ? 1 : 0) + (pinnedNextTrack ? 1 : 0) + globalIdx + 1;
              const s              = t._score || computePriorityScore(t);
              const delta          = computeTrackFrequencyDelta(t, playsMap, musiciansMap, currentDistance);
              const isExplicitNext = nextTrackId != null && String(t.id) === String(nextTrackId);

              return (
                <div key={t.id} style={{
                  padding:7, marginBottom:4, borderRadius:10,
                  background:'#1F1C18', border:'none',
                  borderLeft: t.in_scaletta ? '3px solid #ED4D1D' : '3px solid transparent',
                }}>
                  <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:7 }}>
                    <div style={{ fontSize:18, fontWeight:500, color: t.in_scaletta ? '#fff' : '#706A60', minWidth:14, textAlign:'left', flexShrink:0 }}>
                      {rank}
                    </div>
                    <div onClick={() => setEditTrack(t)} style={{ flex:1, minWidth:0, cursor:'pointer' }}>
                      <div style={{ fontSize:18, fontWeight:400, color:'#E8DDD0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {t.title}{t.artist ? <span style={{ fontWeight:400, color:'#706A60' }}> — {t.artist}</span> : null}
                      </div>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:5 }}>
                    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'space-around', padding:'4px 6px', borderRadius:7, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#706A60" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                        <span style={{ fontSize:17, fontWeight:800, color:'#B5AA9A', lineHeight:1 }}>{s.score}</span>
                      </div>
                      <div style={{ width:1, height:16, background:'rgba(255,255,255,0.07)' }}/>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#706A60" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>
                        <span style={{ fontSize:17, fontWeight:800, color:'#B5AA9A', lineHeight:1 }}>{s.coverage}</span>
                      </div>
                      <div style={{ width:1, height:16, background:'rgba(255,255,255,0.07)' }}/>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#706A60" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                        <span style={{ fontSize:17, fontWeight:800, lineHeight:1, color: delta < 0 ? '#26A69A' : delta > 0 ? '#FFA726' : '#706A60' }}>{delta > 0 ? '+' : ''}{delta}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const newId = String(nextTrackId) === String(t.id) ? null : t.id;
                        setNextTrackId(newId);
                        updateEventNextTrack(eventId, newId).catch(console.error);
                      }}
                      style={{
                        flex:1, padding:'6px 4px', borderRadius:7, minHeight:44,
                        cursor:'pointer', fontFamily:'inherit',
                        fontSize:16, fontWeight:700, lineHeight:1.2,
                        background: isExplicitNext ? '#ED4D1D' : 'rgba(237,77,29,0.06)',
                        color: isExplicitNext ? '#fff' : '#ED4D1D',
                        border: isExplicitNext ? 'none' : '1px solid rgba(237,77,29,0.2)',
                        transition:'all .15s',
                      }}
                    >
                      Prossimo
                    </button>
                  </div>
                </div>
              );
            };

            return (
              <>
                {readyList.map((t, idx) => renderTrackCard(t, idx, true))}

                {draftList.length > 0 && (
                  <>
                    <div style={{ height:'1px', background:'rgba(255,255,255,0.1)', margin:'16px 0 8px' }}/>
                    <button onClick={() => setNonProntiCollapsed(o => !o)} style={{
                      width:'100%', background:'none', border:'none', cursor:'pointer',
                      display:'flex', alignItems:'center', gap:8, padding:'0 0 8px',
                    }}>
                      <span style={{ fontSize:18, fontWeight:800, color:'#E8DDD0', flex:1, textAlign:'left' }}>Non pronti</span>
                      <span style={{ fontSize:15, color:'#706A60' }}>{draftList.length}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#706A60" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition:'transform .2s', transform: nonProntiCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    {!nonProntiCollapsed && draftList.map((t, idx) => renderTrackCard(t, readyList.length + idx, false))}
                  </>
                )}
              </>
            );
          })()}

          {/* Brani suonati — collassabili */}
          {played.length > 0 && (
            <div style={{ marginTop:16 }}>
              <div style={{ height:'1px', background:'rgba(255,255,255,0.1)', margin:'0 0 8px' }}/>
              <button onClick={() => setPlayedCollapsed(o => !o)} style={{
                width:'100%', background:'none', border:'none', cursor:'pointer',
                display:'flex', alignItems:'center', gap:8, padding:'0 0 8px',
              }}>
                <span style={{ fontSize:18, fontWeight:800, color:'#E8DDD0', flex:1, textAlign:'left' }}>Suonati</span>
                <span style={{ fontSize:15, color:'#706A60' }}>{played.length}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#706A60" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition:'transform .2s', transform: playedCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {!playedCollapsed && played.length > 1 && renderSceltaOrdine()}
              {!playedCollapsed && ordinaSuonati(played).map(t => (
                <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', marginBottom:4, borderRadius:10, background:'#1F1C18', opacity:0.6 }}>
                  {/* Toccando la spunta il brano torna fra quelli da suonare.
                      Il quadratino resta piccolo, il bersaglio e' di 44px. */}
                  <button onClick={() => handlePlayed(t)} aria-label="Rimetti fra i brani da suonare" style={{
                    width:44, height:44, margin:'-9px 0 -9px -9px', padding:0, flexShrink:0, cursor:'pointer',
                    background:'none', border:'none', display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <span style={{ width:26, height:26, borderRadius:4, border:'2px solid #ED4D1D', background:'#ED4D1D',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:16, color:'#fff', fontWeight:900, lineHeight:1 }}>&#10003;</span>
                  </button>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:17, fontWeight:700, color:'#B5AA9A', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.title}</div>
                    {t.artist && <div style={{ fontSize:15, color:'#706A60' }}>{t.artist}</div>}
                  </div>
                  {renderContoApplausi(t)}
                </div>
              ))}
            </div>
          )}

          {/* Reset likes */}
          <div style={{ marginTop:32, textAlign:'center' }}>
            {!confirmReset ? (
              <button onClick={() => setConfirmReset(true)} style={{ background:'none', border:'none', color:'#4A4540', fontSize:16, cursor:'pointer', textDecoration:'underline' }}>
                Azzera tutti i like
              </button>
            ) : (
              <div style={{ background:'rgba(160,80,80,0.08)', border:'1px solid rgba(160,80,80,0.2)', borderRadius:10, padding:'14px 16px' }}>
                <div style={{ fontSize:16, color:'#B5AA9A', marginBottom:12 }}>Azzerare tutti i like della serata?</div>
                <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                  <button onClick={() => setConfirmReset(false)} style={{ background:'none', border:'1px solid rgba(237,77,29,0.2)', borderRadius:8, color:'#706A60', fontSize:16, padding:'6px 14px', cursor:'pointer' }}>Annulla</button>
                  <button onClick={handleResetLikes} style={{ background:'rgba(160,80,80,0.15)', border:'1px solid rgba(160,80,80,0.3)', borderRadius:8, color:'#e08080', fontSize:16, padding:'6px 14px', cursor:'pointer', fontWeight:700 }}>Si, azzera</button>
                </div>
              </div>
            )}
            {/* Serve dopo le prove: si applaude per vedere se funziona, e poi
                si riparte da zero quando comincia la serata vera. */}
            {played.some(t => applausiDi(t) > 0) && (
              <button
                onClick={async () => {
                  try { await resetApplause(eventId); setTotaliNoti({}); setMieiApplausi({}); await load(); }
                  catch (e) { console.error(e); }
                }}
                style={{ display:'block', margin:'14px auto 0', background:'none', border:'none', color:'#4A4540', fontSize:16, cursor:'pointer', textDecoration:'underline', fontFamily:'inherit' }}>
                Azzera tutti gli applausi
              </button>
            )}
          </div>
        </div>

        {onExitLive && (
          <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:10, background:'#000000', borderTop:'1px solid rgba(237,77,29,0.2)', padding:'12px 16px', paddingBottom:'calc(12px + env(safe-area-inset-bottom, 0px))', boxSizing:'border-box' }}>
            <button onClick={onExitLive} style={{ padding:'10px 18px', borderRadius:10, background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.14)', color:'#E8DDD0', fontSize:17, fontWeight:700, cursor:'pointer', letterSpacing:'.3px', display:'flex', alignItems:'center', gap:8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Esci
            </button>
          </div>
        )}
      </div>

      {profileModal && <MusicianPopup musician={profileModal} tracks={tracks} onClose={() => setProfileModal(null)}/>}

      {musicianModal && (
        <Modal onClose={() => setMusicianModal(null)} maxWidth={340}>
          <div style={{ padding:'4px 0 8px' }}>
            <div style={{ fontSize:24, fontWeight:800, color:'#E8DDD0', marginBottom:16 }}>{musicianModal.fullName}</div>
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:16, color:'#706A60' }}>Brani</span>
                <span style={{ fontSize:16, fontWeight:800, color:'#E8DDD0' }}>{musicianModal.playedCount}/{musicianModal.bookedCount}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:16, color:'#706A60' }}>Strumenti</span>
                <span style={{ fontSize:16, fontWeight:700, color:'#B5AA9A' }}>{musicianModal.instruments.join(', ') || '—'}</span>
              </div>
            </div>
            <button
              onClick={() => { toggleMusicianFilter(musicianModal.id); setMusicianModal(null); }}
              style={{
                width:'100%', padding:'10px', borderRadius:10, cursor:'pointer',
                fontSize:16, fontWeight:700, transition:'all .15s',
                background: filterMusicianIds.has(musicianModal.id) ? '#ED4D1D' : 'rgba(237,77,29,0.1)',
                color: filterMusicianIds.has(musicianModal.id) ? '#fff' : '#ED4D1D',
                border: filterMusicianIds.has(musicianModal.id) ? 'none' : '1px solid rgba(237,77,29,0.3)',
              }}
            >
              {filterMusicianIds.has(musicianModal.id)
                ? `Rimuovi filtro ${firstName(musicianModal.fullName)}`
                : `Filtra brani di ${firstName(musicianModal.fullName)}`}
            </button>
          </div>
        </Modal>
      )}

      {addTrackOpen && (
        <TrackModal
          musician={currentUser}
          existing={null}
          canManage={true}
          musicians={musicians}
          onClose={() => setAddTrackOpen(false)}
          onSave={async ({ title, artist, key, bpm, role, tonality, titles }) => {
            const base = { event_id: eventId, proposed_by: currentUser.id, position: tracks.length, status: 'auto' };
            if (titles && titles.length) {
              for (const t of titles) await addTrack({ ...base, title: t, artist: '', key: null, bpm: null });
            } else {
              const t = await addTrack({ ...base, title, artist: artist || '', key: key || null, bpm: bpm || null });
              if (role) await joinTrack(t.id, currentUser.id, role, tonality || 'originale');
            }
            setAddTrackOpen(false);
            await load();
          }}
        />
      )}

      {editTrack && (
        <TrackModal
          musician={currentUser}
          existing={editTrack}
          canManage={true}
          musicians={musicians}
          onShowMusician={m => { setEditTrack(null); setProfileModal(m); }}
          onClose={() => setEditTrack(null)}
          onRemovePlayer={async (musicianId) => {
            await leaveTrack(editTrack.id, musicianId);
            await load();
            setEditTrack(prev => prev ? { ...prev, track_players: (prev.track_players || []).filter(p => p.musician_id !== musicianId) } : null);
          }}
          onAddPlayer={async (musicianId, role) => {
            await joinTrack(editTrack.id, musicianId, role, 'originale');
            await load();
          }}
          onDelete={async () => { await removeTrack(editTrack.id); setEditTrack(null); await load(); }}
          onSave={async ({ title, artist, key, bpm, role, tonality }) => {
            await updateTrack(editTrack.id, { title, artist: artist || '', key: key || null, bpm: bpm ? parseInt(bpm) : null });
            if (role) await joinTrack(editTrack.id, currentUser.id, role, tonality);
            setEditTrack(null);
            await load();
          }}
        />
      )}
      </>
    );
  }

  // ── RENDER OSPITE / MUSICISTA NON-ORGANIZZATORE ───────────────────────────
  const sortedUnplayed = sortByPriority(tracks).filter(t => !t.played && t.status !== 'playing');
  const playedList     = tracks.filter(t => t.played);
  const readyList      = sortedUnplayed.filter(t => isTrackReady(t));
  // Il prossimo scelto dall'organizzatore puo' anche non essere "pronto": in
  // quel caso sta nel suo riquadro e non si ripete fra quelli incompleti.
  const draftList      = sortedUnplayed.filter(t => !isTrackReady(t)
    && !(nextTrackId && String(t.id) === String(nextTrackId)));
  // prossimo = solo quello scelto esplicitamente dall'organizzatore
  const nextTrack      = (nextTrackId && sortedUnplayed.find(t => String(t.id) === String(nextTrackId))) || null;
  const otherReady     = readyList.filter(t => t !== nextTrack);

  const renderGuestRow = (t, rank) => {
    const isLiked  = likedIds.has(t.id);
    const score    = computePriorityScore(t).score;
    return (
      <div key={t.id} style={{ padding:7, marginBottom:4, borderRadius:10, background:'#1F1C18', border:'none', display:'flex', alignItems:'center', gap:5 }}>
        <div style={{ fontSize:18, fontWeight:500, color:'#706A60', minWidth:14, textAlign:'left', flexShrink:0 }}>
          {rank}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div onClick={() => setTrackDetail(t)}
            style={{ overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', fontSize:18, fontWeight:400, color:'#E8DDD0', cursor:'pointer' }}>
            {t.title}{t.artist ? <span style={{ color:'#706A60' }}> — {t.artist}</span> : null}
          </div>
        </div>
        <button onClick={() => handleLike(t)} style={{
          display:'flex', alignItems:'center', gap:5, flexShrink:0,
          padding:'6px 11px', borderRadius:10, cursor:'pointer', transition:'all .15s',
          background: isLiked ? '#ED4D1D' : 'rgba(237,77,29,0.06)',
          border: isLiked ? 'none' : '1px solid rgba(237,77,29,0.45)',
          color: '#fff',
        }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
          <span style={{ fontSize:17, fontWeight: isLiked ? 900 : 400, lineHeight:1 }}>{score}</span>
        </button>
      </div>
    );
  };

  return (
    <>
    <div style={{ minHeight:'100vh', background:'#000000', fontFamily:"'Exo 2',sans-serif", color:'#E8DDD0' }}>

      {/* Header — replica esatta topbar app (mobile) */}
      <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:10, background:'#000000', borderBottom:'1px solid rgba(237,77,29,0.06)', height:'calc(80px + max(env(safe-area-inset-top),16px))', paddingTop:'max(env(safe-area-inset-top),16px)', paddingLeft:12, paddingRight:12, display:'flex', alignItems:'center', justifyContent:'space-between', boxSizing:'border-box' }}>
        <JammoMark height={58} style={{ flexShrink:0, cursor: onExitLive ? 'pointer' : 'default' }} onClick={() => onExitLive && onExitLive()}/>
        {!currentUser ? (
          <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0, background:'var(--surface2)', border:'1px solid var(--card-border)', borderRadius:999, padding:'6px 16px 6px 6px' }}>
            <div style={{ width:30, height:30, borderRadius:'50%', background:'var(--grad)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#fff', flexShrink:0 }}>O</div>
            <span style={{ fontSize:16, fontWeight:600, color:'var(--text)' }}>Ospite</span>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0, background:'var(--surface2)', border:'1px solid var(--card-border)', borderRadius:999, padding:'6px 16px 6px 6px' }}>
            <div style={{ width:30, height:30, borderRadius:'50%', background:'var(--grad)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#fff', flexShrink:0 }}>
              {(currentUser.name || 'M').charAt(0).toUpperCase()}
            </div>
            <span style={{ fontSize:16, fontWeight:600, color:'var(--text)', maxWidth:100, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
              {firstName(currentUser.name)}
            </span>
          </div>
        )}
      </div>

      <div style={{ position:'fixed', top:'calc(80px + max(env(safe-area-inset-top),16px))', left:0, right:0, zIndex:9, background:'#ED4D1D', height:26, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
        <span className="live-dot" style={{ width:7, height:7, borderRadius:'50%', background:'#fff', display:'inline-block', flexShrink:0 }}/>
        <span style={{ fontSize:15, fontWeight:900, color:'#fff', letterSpacing:'4px' }}>LIVE</span>
      </div>

      <div style={{ padding:'calc(114px + max(env(safe-area-inset-top),16px)) 14px calc(72px + env(safe-area-inset-bottom, 0px))' }}>

        {/* Hero evento */}
        {(() => {
          return (
            <div style={{ position:'relative', borderRadius:16, overflow:'hidden', border:'1px solid rgba(255,255,255,0.08)', marginBottom:16 }}>
              {heroBg && (
                <img src={heroBg} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', objectPosition:'center', display:'block' }}/>
              )}
              <div style={{ position:'absolute', inset:0, background: heroBg ? 'rgba(18,16,14,0.60)' : '#1F1C18' }}/>
              <div style={{ position:'relative', padding:'28px 20px 24px' }}>
                <div style={{ fontSize:26, fontWeight:900, color:'#E8DDD0', lineHeight:1.1, marginBottom:8 }}>{event.title}</div>
                <div style={{ fontSize:16, color:'rgba(255,255,255,0.6)', lineHeight:1.4 }}>Scegli i tuoi brani preferiti</div>
              </div>
            </div>
          );
        })()}

        {/* IN ESECUZIONE */}
        {playingTrack && (
          <div onClick={() => setTrackDetail(playingTrack)} style={{ marginBottom:4, padding:7, paddingBottom:20, borderRadius:14, background:'rgba(31,28,24,1)', border:'none', cursor:'pointer' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginBottom:10 }}>
              <span className="live-dot" style={{ width:7, height:7, borderRadius:'50%', background:'#ED4D1D', display:'inline-block', flexShrink:0 }}/>
              <span style={{ fontSize:13, fontWeight:800, color:'#ED4D1D', letterSpacing:'2px' }}>ON STAGE</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:5 }}>
              <div style={{ fontSize:18, fontWeight:500, color:'#706A60', minWidth:14, flexShrink:0 }}>{played.length + 1}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:18, fontWeight:700, color:'#E8DDD0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {playingTrack.title}
                  {playingTrack.artist && <span style={{ fontWeight:400, color:'#706A60' }}> — {playingTrack.artist}</span>}
                </div>
              </div>
            </div>
            {renderApplausi(playingTrack, true)}
          </div>
        )}

        {/* PROSSIMO */}
        {nextTrack && (() => {
          const players = (nextTrack.track_players || []).filter(p => p.role);
          return (
            <div onClick={() => setTrackDetail(nextTrack)} style={{
              marginBottom:4, padding:7, paddingBottom:20, borderRadius:14,
              background:'rgba(31,28,24,1)', border:'none',
              cursor:'pointer',
            }}>
              <div style={{ fontSize:13, fontWeight:800, color:'#ED4D1D', letterSpacing:'2px', textAlign:'center', marginBottom:10 }}>
                PROSSIMO BRANO
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                <div style={{ fontSize:18, fontWeight:500, color:'#706A60', minWidth:14, flexShrink:0 }}>{played.length + (playingTrack ? 1 : 0) + 1}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:18, fontWeight:700, color:'#E8DDD0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {nextTrack.title}
                    {nextTrack.artist && <span style={{ fontWeight:400, color:'#706A60' }}> — {nextTrack.artist}</span>}
                  </div>
                </div>
              </div>
              {players.length > 0 && (
                <div style={{ marginTop:10, paddingLeft:30 }}>
                  {[...players]
                    .sort((a, b) => {
                      const ai = PLAYER_ORDER.indexOf(normalizeInstrument(a.role));
                      const bi = PLAYER_ORDER.indexOf(normalizeInstrument(b.role));
                      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                    })
                    .map(p => {
                      const name = p.musicians?.name || nameMap[String(p.musician_id)] || 'Musicista';
                      const role = p.role.replace('(alt)', '').trim();
                      const isMe = currentUser && String(p.musician_id) === String(currentUser.id);
                      return (
                        <div key={p.musician_id} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                          {role && <span style={{ fontSize:15, fontWeight:400, color:'rgba(255,255,255,0.65)' }}>{role}</span>}
                          <span style={{ fontSize:17, fontWeight:400, color:'#fff', lineHeight:1.3 }}>{firstName(name)}</span>
                          {isMe && (
                            <span style={{ display:'inline-block', fontSize:13, fontWeight:700, background:'#ED4D1D', color:'#fff', padding:'3px 8px', borderRadius:4, marginLeft:4 }}>
                              PREPARATI
                            </span>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })()}

        {otherReady.map((t, idx) => renderGuestRow(t, played.length + (playingTrack ? 1 : 0) + (nextTrack ? 1 : 0) + idx + 1))}

        {/* Sezione Cercasi musicisti */}
        {draftList.length > 0 && (
          <>
            <div style={{ margin:'20px 0 10px' }}>
              <div style={{ fontSize:18, fontWeight:800, color:'#E8DDD0' }}>Cercasi musicisti</div>
              <div style={{ fontSize:16, color:'#B5AA9A', marginTop:3 }}>Almeno voce e chitarra o Piano/Keys</div>
            </div>
            {draftList.map((t, idx) => {
              const rank = played.length + (playingTrack ? 1 : 0) + readyList.length + idx + 1;
              const isLiked = likedIds.has(t.id);
              const score = computePriorityScore(t).score;
              const roles = (t.track_players || []).filter(p => p.role).map(p => p.role.replace('(alt)', '').trim());
              return (
                <div key={t.id} onClick={() => setTrackDetail(t)} style={{ padding:'5px 8px', marginBottom:4, borderRadius:10, background:'#1F1C18', border:'none', display:'flex', alignItems:'center', gap:5, cursor:'pointer' }}>
                  <div style={{ fontSize:16, fontWeight:500, color:'#706A60', minWidth:14, textAlign:'left', flexShrink:0 }}>{rank}</div>
                  <div style={{ flex:1, minWidth:0, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', fontSize:18, fontWeight:400, color:'#E8DDD0' }}>
                    {t.title}{t.artist ? <span style={{ color:'#706A60' }}> — {t.artist}</span> : null}
                  </div>
                  <div style={{ display:'flex', gap:4, flexShrink:0, alignItems:'center' }}>
                    {DRAFT_SLOTS.map(slot => {
                      const filled = roles.some(r => slot.match(r));
                      return (
                        <div key={slot.label} title={slot.label} style={{
                          width:28, height:28, borderRadius:7, flexShrink:0,
                          display:'flex', alignItems:'center', justifyContent:'center',
                          background:'rgba(255,255,255,0.04)',
                          border: `1.5px solid ${filled ? '#26A69A' : '#ED4D1D'}`,
                        }}>
                          <img src={slot.icon} alt={slot.label} style={{ width:22, height:22, objectFit:'contain', filter:'invert(1)', pointerEvents:'none' }}/>
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={e => { e.stopPropagation(); handleLike(t); }} style={{
                    display:'flex', alignItems:'center', gap:5, flexShrink:0,
                    padding:'6px 11px', borderRadius:10, cursor:'pointer', transition:'all .15s',
                    background: isLiked ? '#ED4D1D' : 'rgba(237,77,29,0.06)',
                    border: isLiked ? 'none' : '1px solid rgba(237,77,29,0.45)',
                    color: '#fff',
                  }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                    <span style={{ fontSize:17, fontWeight: isLiked ? 900 : 400, lineHeight:1 }}>{score}</span>
                  </button>
                </div>
              );
            })}
          </>
        )}

        {/* Brani suonati */}
        {playedList.length > 0 && (
          <>
            <div style={{ margin:'20px 0 10px' }}>
              <div style={{ fontSize:18, fontWeight:800, color:'#E8DDD0' }}>Brani suonati</div>
            </div>
            {playedList.length > 1 && renderSceltaOrdine()}
            {ordinaSuonati(playedList).map(t => (
              <div key={t.id} style={{
                display:'flex', alignItems:'center', gap:10,
                padding:'9px 12px', marginBottom:5, borderRadius:10,
                background:'#1F1C18', border:'none',
              }}>
                <span style={{ fontSize:17, color:'#26A69A', flexShrink:0, fontWeight:700 }}>&#10003;</span>
                <div style={{ flex:1, minWidth:0, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', fontSize:17, fontWeight:600, color:'#B5AA9A' }}>
                  {t.title}{t.artist ? <span style={{ fontWeight:400, color:'#706A60' }}>{` — ${t.artist}`}</span> : ''}
                </div>
                {renderContoApplausi(t)}
              </div>
            ))}
          </>
        )}

        {/* Conversione — solo per chi arriva dal QR senza account.
            Chi ha appena votato e' nel momento di massima disponibilita':
            fino a ieri qui non gli veniva proposto niente. */}
        {!currentUser && (
          <div style={{ marginTop:32, paddingTop:24, borderTop:'1px solid rgba(255,255,255,0.08)' }}>

            <div style={{ padding:'20px 18px', borderRadius:14, background:'#1F1C18', border:'1px solid rgba(237,77,29,0.25)', marginBottom:12 }}>
              <div style={{ fontSize:20, fontWeight:800, color:'#E8DDD0', marginBottom:8 }}>
                Suoni uno strumento?
              </div>
              <div style={{ fontSize:17, color:'#B5AA9A', lineHeight:1.6, marginBottom:16 }}>
                I brani che vedi qui li hanno proposti i musicisti sul palco.
                Su Jammo scegli in quali suonare, proponi i tuoi, e la scaletta
                la costruite insieme prima della serata.
              </div>
              <button onClick={vaiAllaRegistrazione} style={{
                width:'100%', padding:'14px', borderRadius:12, border:'none',
                background:'#ED4D1D', color:'#fff', fontSize:18, fontWeight:800,
                cursor:'pointer', minHeight:44, fontFamily:"'Exo 2',sans-serif",
              }}>
                Crea il tuo profilo
              </button>
            </div>

            <div style={{ padding:'18px', borderRadius:14, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ fontSize:18, fontWeight:700, color:'#E8DDD0', marginBottom:6 }}>
                Organizzi serate musicali?
              </div>
              <div style={{ fontSize:16, color:'#B5AA9A', lineHeight:1.6, marginBottom:14 }}>
                Questa pagina, il QR code e la gestione del live sono parte di Jammo.
                Gratis, e in continua evoluzione.
              </div>
              <button onClick={vaiAllaRegistrazione} style={{
                width:'100%', padding:'12px', borderRadius:12,
                background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.15)',
                color:'#E8DDD0', fontSize:17, fontWeight:600,
                cursor:'pointer', minHeight:44, fontFamily:"'Exo 2',sans-serif",
              }}>
                Scopri come funziona
              </button>
            </div>

          </div>
        )}

      </div>

      {onExitLive && (
        <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:10, background:'#000000', borderTop:'1px solid rgba(237,77,29,0.2)', padding:'12px 16px', paddingBottom:'calc(12px + env(safe-area-inset-bottom, 0px))', boxSizing:'border-box' }}>
          <button onClick={onExitLive} style={{ padding:'10px 18px', borderRadius:10, background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.14)', color:'#E8DDD0', fontSize:17, fontWeight:700, cursor:'pointer', letterSpacing:'.3px', display:'flex', alignItems:'center', gap:8 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Esci
          </button>
        </div>
      )}
    </div>


    {profileModal && <MusicianPopup musician={profileModal} tracks={tracks} onClose={() => setProfileModal(null)}/>}

    {trackDetail && (
      <Modal onClose={() => setTrackDetail(null)} maxWidth={360} centered>
        <div style={{ padding:'4px 0 8px' }}>
          <div style={{ fontSize:22, fontWeight:900, color:'#E8DDD0', lineHeight:1.2, marginBottom:4 }}>{trackDetail.title}</div>
          {trackDetail.artist && <div style={{ fontSize:16, color:'#706A60', marginBottom:16 }}>{trackDetail.artist}</div>}
          {(trackDetail.key || trackDetail.bpm) && (
            <div style={{ fontSize:15, color:'#B5AA9A', marginBottom:16 }}>
              {[trackDetail.key, trackDetail.bpm ? `${trackDetail.bpm} bpm` : null].filter(Boolean).join(' • ')}
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[...(trackDetail.track_players || []).filter(p => p.role)]
              .sort((a, b) => {
                const ai = PLAYER_ORDER.indexOf(normalizeInstrument(a.role));
                const bi = PLAYER_ORDER.indexOf(normalizeInstrument(b.role));
                return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
              })
              .map(p => {
                const name = p.musicians?.name || nameMap[String(p.musician_id)] || 'Musicista';
                const role = p.role.replace('(alt)', '').trim();
                const fullMusician = musicians.find(mus => String(mus.id) === String(p.musician_id));
                return (
                  <div key={p.musician_id}
                    onClick={() => { if (fullMusician) { setTrackDetail(null); setProfileModal(fullMusician); } }}
                    style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 12px', background:'var(--surface2)', borderRadius:8, cursor: fullMusician ? 'pointer' : 'default' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      {fullMusician?.avatar_url
                        ? <img src={fullMusician.avatar_url} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover',flexShrink:0}}/>
                        : <div className="avatar" style={{width:36,height:36,fontSize:14,flexShrink:0}}>{name.charAt(0)}</div>
                      }
                      <span style={{ fontSize:18, fontWeight:800, color:'#E8DDD0' }}>{firstName(name)}</span>
                    </div>
                    {role && <span style={{ fontSize:16, color:'#B5AA9A' }}>{role}</span>}
                  </div>
                );
              })}
            {(trackDetail.track_players || []).filter(p => p.role).length === 0 && (
              <div style={{ textAlign:'center', color:'#706A60', fontSize:16, padding:'8px 0' }}>Nessun musicista ancora</div>
            )}
          </div>
        </div>
      </Modal>
    )}
    </>
  );
}

export default LiveView;
